/* eslint-disable @typescript-eslint/ban-ts-comment -- legacy docking tree engine */
// @ts-nocheck
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../ui/index.ts";
import { I, type IconProps } from "../icons/icons.tsx";
import { Widgets } from "../../dashboard/widgets.ts";
import type {
  DockNode,
  DockingContextValue,
  DockingProviderProps,
  DragInfo,
} from "./docking.types.ts";
// ====== DOCKING SYSTEM (pointer-based DnD with nested tab groups) ======
//
// Tree:
//   - 'split': { type:'split', dir, sizes, children: Node[] }
//   - 'tabs':  { type:'tabs',  active, items: Item[] }   // items = panel ids OR nested 'tabs' nodes
// An Item is either a string (panel id) or a 'tabs' node (recursive — onglets dans un onglet).

interface PanelDef {
  id: string;
  title: string;
  icon: (p: IconProps) => React.ReactElement;
  render: (props: { panelId: string }) => React.ReactElement;
  noPadding?: boolean;
  multiInstance?: boolean; // can be added multiple times
}

const PanelDefs: Record<string, PanelDef> = (() => {
  const W = Widgets;
  return {
    agenda: {
      id: "agenda",
      title: "Patients du jour",
      icon: I.CalendarDay,
      render: () => <W.PatientsTodayWidget />,
    },
    "current-patient": {
      id: "current-patient",
      title: "Patient courant",
      icon: I.User,
      render: () => <W.CurrentPatientWidget />,
    },
    "clinical-comments": {
      id: "clinical-comments",
      title: "Commentaires cliniques",
      icon: I.FileText,
      render: () => <W.ClinicalCommentsWidget />,
    },
    diagnostic: {
      id: "diagnostic",
      title: "Diagnostic",
      icon: I.Stethoscope,
      render: () => <W.DiagnosticWidget />,
    },
    "treatment-plan": {
      id: "treatment-plan",
      title: "Plan de traitement",
      icon: I.ListChecks,
      render: () => <W.TreatmentPlanWidget />,
    },
    objectives: {
      id: "objectives",
      title: "Objectifs",
      icon: I.Target,
      render: () => <W.ObjectivesWidget />,
    },
    "patient-files": {
      id: "patient-files",
      title: "Fichiers du patient",
      icon: I.FolderOpen,
      render: () => <W.PatientFilesWidget />,
      noPadding: true,
    },
    "media-viewer": {
      id: "media-viewer",
      title: "Visionneur",
      icon: I.Image,
      render: () => <W.MediaViewerWidget />,
      noPadding: true,
      multiInstance: true,
    },
    "patient-chat": {
      id: "patient-chat",
      title: "Chat patient",
      icon: I.Message,
      render: () => <W.PatientChatWidget />,
      noPadding: true,
    },
    "patient-rdv": {
      id: "patient-rdv",
      title: "RDV du patient",
      icon: I.Calendar,
      render: () => <W.PatientRdvWidget />,
    },
    "patient-notes": {
      id: "patient-notes",
      title: "Notes patient",
      icon: I.FileText,
      render: () => <W.PatientNotesWidget />,
    },
    "patient-timeline": {
      id: "patient-timeline",
      title: "Historique patient",
      icon: I.Clock,
      render: () => <W.PatientTimelineWidget />,
    },
    stats: {
      id: "stats",
      title: "Indicateurs",
      icon: I.Activity,
      render: () => <W.StatsWidget />,
    },
    messages: {
      id: "messages",
      title: "Messages patients",
      icon: I.Message,
      render: () => <W.MessagesWidget />,
    },
    activity: {
      id: "activity",
      title: "Activité de la semaine",
      icon: I.TrendUp,
      render: () => <W.ActivityWidget />,
    },
    contacts: {
      id: "contacts",
      title: "Premiers contacts",
      icon: I.Phone,
      render: () => <W.FirstContactWidget />,
    },
    tasks: {
      id: "tasks",
      title: "Tâches du jour",
      icon: I.Check,
      render: () => <W.TasksWidget />,
    },
    "agenda-grid": {
      id: "agenda-grid",
      title: "Patients du jour (grille)",
      icon: I.LayoutGrid,
      render: ({ panelId }) => <W.PatientsTodayGridWidget panelId={panelId} />,
      multiInstance: true,
      noPadding: true,
    },
    "tasks-grid": {
      id: "tasks-grid",
      title: "Tâches (grille)",
      icon: I.LayoutGrid,
      render: ({ panelId }) => <W.TasksGridWidget panelId={panelId} />,
      multiInstance: true,
      noPadding: true,
    },
    "messages-grid": {
      id: "messages-grid",
      title: "Messages (grille)",
      icon: I.LayoutGrid,
      render: ({ panelId }) => <W.MessagesGridWidget panelId={panelId} />,
      multiInstance: true,
      noPadding: true,
    },
    "contacts-grid": {
      id: "contacts-grid",
      title: "Premiers contacts (grille)",
      icon: I.LayoutGrid,
      render: ({ panelId }) => <W.FirstContactGridWidget panelId={panelId} />,
      multiInstance: true,
      noPadding: true,
    },
    "patient-rdv-grid": {
      id: "patient-rdv-grid",
      title: "RDV patient (grille)",
      icon: I.LayoutGrid,
      render: ({ panelId }) => <W.PatientRdvGridWidget panelId={panelId} />,
      multiInstance: true,
      noPadding: true,
    },
    "clinical-comments-grid": {
      id: "clinical-comments-grid",
      title: "Commentaires (grille)",
      icon: I.LayoutGrid,
      render: ({ panelId }) => (
        <W.ClinicalCommentsGridWidget panelId={panelId} />
      ),
      multiInstance: true,
      noPadding: true,
    },
    "patient-civil": {
      id: "patient-civil",
      title: "État civil",
      icon: I.User,
      render: () => <W.PatientCivilWidget />,
    },
    "patient-billing": {
      id: "patient-billing",
      title: "Facturation",
      icon: I.CreditCard,
      render: () => <W.PatientBillingWidget />,
    },
    "patient-history": {
      id: "patient-history",
      title: "Historique",
      icon: I.Clock,
      render: () => <W.PatientHistoryWidget />,
    },
    agenda_full: {
      id: "agenda_full",
      title: "Agenda complet",
      icon: I.Calendar,
      render: () => <W.AgendaWidget />,
      noPadding: true,
    },
  };
})();
export { PanelDefs };

// ====== BOUTIQUE CATALOG ======
// Groups panels that have multiple variants (e.g., simple + advanced grid).
// Single-variant panels are auto-generated from PanelDefs.

export interface BoutiqueVariant {
  panelId: string;
  label: string;
}

export interface BoutiqueEntry {
  groupId: string;
  title: string;
  icon: (p: IconProps) => React.ReactElement;
  variants: BoutiqueVariant[];
}

const VARIANT_GROUPS: BoutiqueEntry[] = [
  {
    groupId: "agenda",
    title: "Patients du jour",
    icon: I.CalendarDay,
    variants: [
      { panelId: "agenda", label: "Liste simple" },
      { panelId: "agenda-grid", label: "Grille avancée" },
    ],
  },
  {
    groupId: "tasks",
    title: "Tâches du jour",
    icon: I.Check,
    variants: [
      { panelId: "tasks", label: "Liste simple" },
      { panelId: "tasks-grid", label: "Grille avancée" },
    ],
  },
  {
    groupId: "messages",
    title: "Messages patients",
    icon: I.Message,
    variants: [
      { panelId: "messages", label: "Liste simple" },
      { panelId: "messages-grid", label: "Grille avancée" },
    ],
  },
  {
    groupId: "contacts",
    title: "Premiers contacts",
    icon: I.Phone,
    variants: [
      { panelId: "contacts", label: "Liste simple" },
      { panelId: "contacts-grid", label: "Grille avancée" },
    ],
  },
  {
    groupId: "patient-rdv",
    title: "RDV du patient",
    icon: I.Calendar,
    variants: [
      { panelId: "patient-rdv", label: "Liste simple" },
      { panelId: "patient-rdv-grid", label: "Grille avancée" },
    ],
  },
  {
    groupId: "clinical-comments",
    title: "Commentaires cliniques",
    icon: I.FileText,
    variants: [
      { panelId: "clinical-comments", label: "Liste simple" },
      { panelId: "clinical-comments-grid", label: "Grille avancée" },
    ],
  },
];

export function getBoutiqueEntries(): BoutiqueEntry[] {
  const groupByPanelId = new Map<string, BoutiqueEntry>();
  for (const g of VARIANT_GROUPS) {
    for (const v of g.variants) groupByPanelId.set(v.panelId, g);
  }
  const emitted = new Set<string>();
  const entries: BoutiqueEntry[] = [];
  for (const p of Object.values(PanelDefs)) {
    if (/-\d+$/.test(p.id)) continue; // skip dynamically-created instances
    const group = groupByPanelId.get(p.id);
    if (group) {
      if (!emitted.has(group.groupId)) {
        emitted.add(group.groupId);
        entries.push(group);
      }
    } else {
      entries.push({
        groupId: p.id,
        title: p.title,
        icon: p.icon,
        variants: [{ panelId: p.id, label: "" }],
      });
    }
  }
  return entries.sort((a, b) =>
    a.title.localeCompare(b.title, "fr", { sensitivity: "base" }),
  );
}

/** Creates a new instance of a multiInstance panel and returns its unique id. */
export function createPanelInstance(baseId: string): string {
  const base = PanelDefs[baseId];
  if (!base) return baseId;
  // Find truly free slot: base-2, base-3, …
  let n = 2;
  let id = `${baseId}-${n}`;
  while (PanelDefs[id]) id = `${baseId}-${++n}`;
  PanelDefs[id] = { ...base, id };
  return id;
}

let __nid = 0;
const nid = (p = "n") => `${p}${++__nid}`;

function makeTabs(items, active = 0, label = null) {
  return { id: nid("tabs"), type: "tabs", active, items: items.slice(), label };
}
function makeSplit(dir, children, sizes) {
  return {
    id: nid("split"),
    type: "split",
    dir,
    sizes: sizes || children.map(() => 1 / children.length),
    children,
  };
}

// ----- Tree helpers -----
function isItemPanel(it) {
  return typeof it === "string";
}
function isItemSplit(it) {
  return typeof it === "object" && it !== null && it.type === "split";
}
function itemIcon(it) {
  if (isItemPanel(it)) return PanelDefs[it]?.icon;
  if (isItemSplit(it)) return I.Image;
  // nested group icon
  return I.LayoutGrid;
}
function itemTitle(it) {
  if (isItemPanel(it)) return PanelDefs[it]?.title || it;
  if (it.label) return it.label;
  if (isItemSplit(it)) return "Médias";
  // nested group title = active sub-item title
  const sub = it.items[it.active] || it.items[0];
  return sub ? `▸ ${itemTitle(sub)}` : "Groupe";
}

// Find a tabs node by id (could be top-level or nested)
function findTabs(node, tabsId) {
  if (node.type === "tabs") {
    if (node.id === tabsId) return node;
    for (const it of node.items) {
      if (!isItemPanel(it)) {
        const r = findTabs(it, tabsId);
        if (r) return r;
      }
    }
    return null;
  }
  if (node.type === "split") {
    for (const c of node.children) {
      const r = findTabs(c, tabsId);
      if (r) return r;
    }
  }
  return null;
}
// Walk and collect ALL panel ids
function collectPanelIds(node, set = new Set()) {
  if (node.type === "tabs") {
    for (const it of node.items) {
      if (isItemPanel(it)) set.add(it);
      else if (isItemSplit(it) || it.type === "tabs") collectPanelIds(it, set);
    }
  } else if (node.type === "split") {
    node.children.forEach((c) => collectPanelIds(c, set));
  }
  return set;
}

// Remove a panel by id from anywhere. Prunes empty tabs and 1-item splits.
function removePanel(root, panelId) {
  function visit(node) {
    if (node.type === "tabs") {
      const items = [];
      for (const it of node.items) {
        if (isItemPanel(it)) {
          if (it !== panelId) items.push(it);
        } else {
          const cleaned = visit(it);
          if (cleaned) {
            // Unwrap nested tabs that became single-item
            if (cleaned.type === "tabs" && cleaned.items.length === 1) {
              items.push(cleaned.items[0]);
            } else {
              items.push(cleaned);
            }
          }
        }
      }
      if (items.length === 0) return null;
      const active = Math.min(node.active, items.length - 1);
      return { ...node, items, active };
    }
    if (node.type === "split") {
      const newChildren = [];
      for (const c of node.children) {
        const v = visit(c);
        if (v) newChildren.push(v);
      }
      if (newChildren.length === 0) return null;
      if (newChildren.length === 1) return newChildren[0];
      const sizes = redistribute(node.children, newChildren, node.sizes);
      return { ...node, children: newChildren, sizes };
    }
    return node;
  }
  return visit(root) || makeTabs(["agenda"]);
}

function removeGroupItem(root, groupId) {
  function visit(node) {
    if (node.type === "tabs") {
      const items = [];
      for (const it of node.items) {
        if (!isItemPanel(it) && it.id === groupId) continue;
        if (!isItemPanel(it)) {
          const cleaned = visit(it);
          if (cleaned) {
            if (cleaned.type === "tabs" && cleaned.items.length === 1) {
              items.push(cleaned.items[0]);
            } else {
              items.push(cleaned);
            }
          }
        } else {
          items.push(it);
        }
      }
      if (items.length === 0) return null;
      return {
        ...node,
        items,
        active: Math.min(node.active, items.length - 1),
      };
    }
    if (node.type === "split") {
      const newChildren = [];
      for (const c of node.children) {
        const v = visit(c);
        if (v) newChildren.push(v);
      }
      if (newChildren.length === 0) return null;
      if (newChildren.length === 1) return newChildren[0];
      const sizes = redistribute(node.children, newChildren, node.sizes);
      return { ...node, children: newChildren, sizes };
    }
    return node;
  }
  return visit(root) || makeTabs(["agenda"]);
}

/** Removes a tabs node by id at any depth (inside a split, or nested in another tabs). */
function removeTabsNode(root, tabsId) {
  function visit(node) {
    if (node.type === "tabs") {
      if (node.id === tabsId) return null;
      const items = [];
      for (const it of node.items) {
        if (isItemPanel(it)) {
          items.push(it);
        } else {
          const v = visit(it);
          if (v) items.push(v);
        }
      }
      if (items.length === 0) return null;
      return {
        ...node,
        items,
        active: Math.min(node.active, items.length - 1),
      };
    }
    if (node.type === "split") {
      const newChildren = [];
      for (const c of node.children) {
        const v = visit(c);
        if (v) newChildren.push(v);
      }
      if (newChildren.length === 0) return null;
      if (newChildren.length === 1) return newChildren[0];
      const sizes = redistribute(node.children, newChildren, node.sizes);
      return { ...node, children: newChildren, sizes };
    }
    return node;
  }
  return visit(root) || makeTabs(["agenda"]);
}

function redistribute(oldChildren, newChildren, oldSizes) {
  const keptSizes = newChildren.map((c) => {
    const oi = oldChildren.findIndex((o) => o.id === c.id);
    return oi >= 0 ? oldSizes[oi] : 1 / newChildren.length;
  });
  const sum = keptSizes.reduce((a, b) => a + b, 0) || 1;
  return keptSizes.map((s) => s / sum);
}

function addGroupItemToTabs(root, targetTabsId, groupItem) {
  function visit(node) {
    if (node.type === "tabs") {
      if (node.id === targetTabsId) {
        const existingIdx = node.items.findIndex(
          (it) => !isItemPanel(it) && it.id === groupItem.id,
        );
        if (existingIdx >= 0) return { ...node, active: existingIdx };
        return {
          ...node,
          items: [...node.items, groupItem],
          active: node.items.length,
        };
      }
      return {
        ...node,
        items: node.items.map((it) => (isItemPanel(it) ? it : visit(it))),
      };
    }
    if (node.type === "split")
      return { ...node, children: node.children.map(visit) };
    return node;
  }
  return visit(root);
}

// Add a panel (as new item) to a tabs container at end
function addPanelToTabs(root, targetTabsId, panelId) {
  function visit(node) {
    if (node.type === "tabs") {
      if (node.id === targetTabsId) {
        // already there?
        const existingIdx = node.items.findIndex(
          (it) => isItemPanel(it) && it === panelId,
        );
        if (existingIdx >= 0) return { ...node, active: existingIdx };
        return {
          ...node,
          items: [...node.items, panelId],
          active: node.items.length,
        };
      }
      return {
        ...node,
        items: node.items.map((it) => (isItemPanel(it) ? it : visit(it))),
      };
    }
    if (node.type === "split")
      return { ...node, children: node.children.map(visit) };
    return node;
  }
  return visit(root);
}

// Group: drop panelId onto a specific tab item (by tabsId + itemIndex) → wrap them into nested tabs.
// Behaviour:
//  - If target item is already a nested tabs, just append into it.
//  - Else, replace the item with a new nested tabs containing [oldItem, panelId].
function groupWithItem(root, targetTabsId, targetItemIndex, panelId) {
  function visit(node) {
    if (node.type === "tabs") {
      if (node.id === targetTabsId) {
        const items = node.items.slice();
        const target = items[targetItemIndex];
        if (!target) return node;
        if (!isItemPanel(target)) {
          // Already a group → append into it (recursively)
          items[targetItemIndex] = {
            ...target,
            items: [...target.items, panelId],
            active: target.items.length,
          };
        } else {
          // Wrap target panel + new panel into a fresh nested tabs node
          items[targetItemIndex] = makeTabs([target, panelId], 1);
        }
        return { ...node, items, active: targetItemIndex };
      }
      return {
        ...node,
        items: node.items.map((it) => (isItemPanel(it) ? it : visit(it))),
      };
    }
    if (node.type === "split")
      return { ...node, children: node.children.map(visit) };
    return node;
  }
  return visit(root);
}

// Split outer container by placing the group item (tabs node) on one edge
function splitAtNodeWithGroup(root, targetId, edge, groupItem) {
  const dir = edge === "left" || edge === "right" ? "h" : "v";
  const before = edge === "left" || edge === "top";

  function visit(node) {
    if (node.id === targetId) {
      const children = before ? [groupItem, node] : [node, groupItem];
      return makeSplit(dir, children, [0.4, 0.6]);
    }
    if (node.type === "split")
      return { ...node, children: node.children.map(visit) };
    if (node.type === "tabs")
      return {
        ...node,
        items: node.items.map((it) => (isItemPanel(it) ? it : visit(it))),
      };
    return node;
  }
  return visit(root);
}

// Split outer container by inserting a new tabs containing panelId on one edge
function splitAtNode(root, targetId, edge, panelId) {
  const dir = edge === "left" || edge === "right" ? "h" : "v";
  const before = edge === "left" || edge === "top";
  const newTabs = makeTabs([panelId]);

  function visit(node) {
    if (node.id === targetId) {
      const children = before ? [newTabs, node] : [node, newTabs];
      return makeSplit(dir, children, [0.4, 0.6]);
    }
    if (node.type === "split")
      return { ...node, children: node.children.map(visit) };
    if (node.type === "tabs")
      return {
        ...node,
        items: node.items.map((it) => (isItemPanel(it) ? it : visit(it))),
      };
    return node;
  }
  return visit(root);
}

function updateTabs(root, id, fn) {
  if (root.type === "tabs") {
    if (root.id === id) return fn(root);
    return {
      ...root,
      items: root.items.map((it) =>
        isItemPanel(it) ? it : updateTabs(it, id, fn),
      ),
    };
  }
  if (root.type === "split")
    return {
      ...root,
      children: root.children.map((c) => updateTabs(c, id, fn)),
    };
  return root;
}
function updateSplit(root, id, fn) {
  if (root.type === "split" && root.id === id) return fn(root);
  if (root.type === "split")
    return {
      ...root,
      children: root.children.map((c) => updateSplit(c, id, fn)),
    };
  if (root.type === "tabs")
    return {
      ...root,
      items: root.items.map((it) =>
        isItemPanel(it) ? it : updateSplit(it, id, fn),
      ),
    };
  return root;
}
function findFirstTabs(node) {
  if (node.type === "tabs") return node;
  if (node.type === "split")
    for (const c of node.children) {
      const f = findFirstTabs(c);
      if (f) return f;
    }
  return null;
}

const DragCtx = React.createContext<DockingContextValue | null>(null);

export function useDragCtx(): DockingContextValue {
  const ctx = React.useContext(DragCtx);
  if (!ctx) throw new Error("useDragCtx must be used within DockingProvider");
  return ctx;
}

const STORAGE_VERSION = 1;

function loadStoredRoot(storageKey: string | undefined): DockNode | null {
  if (!storageKey || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v?: number; root?: DockNode };
    if (parsed?.v !== STORAGE_VERSION || !parsed.root) return null;
    return parsed.root;
  } catch {
    return null;
  }
}

function DockingProvider({
  initial,
  storageKey,
  children,
}: DockingProviderProps) {
  const [root, setRoot] = React.useState<DockNode>(
    () => loadStoredRoot(storageKey) ?? initial,
  );

  const initialRef = React.useRef(initial);
  React.useEffect(() => {
    initialRef.current = initial;
  }, [initial]);

  const [drag, setDrag] = React.useState<DragInfo | null>(null);
  const [hover, setHover] = React.useState<Record<string, unknown> | null>(
    null,
  );
  const [editMode, setEditMode] = React.useState(false);
  const editSnapshotRef = React.useRef<DockNode | null>(null);
  const rootRef = React.useRef(root);
  React.useEffect(() => {
    rootRef.current = root;
  }, [root]);
  // Capture root the moment edit mode is entered (for the Cancel action).
  React.useEffect(() => {
    if (editMode) {
      editSnapshotRef.current = rootRef.current;
    } else {
      editSnapshotRef.current = null;
    }
  }, [editMode]);
  // Persistance manuelle : seules les sorties explicites du mode édition
  // (Terminer / navigation hors page pendant l'édition) sauvegardent en
  // localStorage. Les changements pendant l'édition restent en mémoire ; un
  // rechargement les perd (avec confirmation beforeunload).
  const editModeRef = React.useRef(editMode);
  React.useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  const persistRoot = React.useCallback(
    (r: DockNode) => {
      if (!storageKey || typeof localStorage === "undefined") return;
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ v: STORAGE_VERSION, root: r }),
        );
      } catch {
        /* quota or serialization failure — non-fatal */
      }
    },
    [storageKey],
  );

  // Auto-save en cas de démontage pendant édition (ex. navigation in-app).
  React.useEffect(() => {
    return () => {
      if (editModeRef.current) persistRoot(rootRef.current);
    };
  }, [persistRoot]);

  // Warning beforeunload pendant l'édition (rechargement / fermeture onglet).
  React.useEffect(() => {
    if (!editMode) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [editMode]);
  // hover shapes:
  //  { kind:'group', tabsId, edge:'top'|'right'|'bottom'|'left'|'center' }    — outer panel-group dock
  //  { kind:'tab',   tabsId, itemIndex }                                       — drop on a tab to nest

  const groupRectsRef = React.useRef(new Map<string, HTMLElement>());
  const tabRectsRef = React.useRef(
    new Map<string, { tabsId: string; idx: number; el: HTMLElement }>(),
  );

  const registerGroup = React.useCallback(
    (tabsId: string, el: HTMLElement | null) => {
      if (el) groupRectsRef.current.set(tabsId, el);
      else groupRectsRef.current.delete(tabsId);
    },
    [],
  );
  const registerTab = React.useCallback(
    (tabsId: string, idx: number, el: HTMLElement | null) => {
      const k = `${tabsId}:${idx}`;
      if (el) tabRectsRef.current.set(k, { tabsId, idx, el });
      else tabRectsRef.current.delete(k);
    },
    [],
  );

  const hoverRef = React.useRef(null);
  React.useEffect(() => {
    hoverRef.current = hover;
  }, [hover]);

  // FLIP animation: snapshot rects keyed by DOM element so reorders animate smoothly.
  const flipPendingRef = React.useRef(null);
  const snapshotTabRects = React.useCallback((tabsId) => {
    const m = new Map();
    for (const info of tabRectsRef.current.values()) {
      if (info.tabsId === tabsId)
        m.set(info.el, info.el.getBoundingClientRect());
    }
    flipPendingRef.current = m;
  }, []);

  // After any reorder, run FLIP from snapshotted rects to current.
  React.useLayoutEffect(() => {
    const old = flipPendingRef.current;
    if (!old) return;
    flipPendingRef.current = null;
    for (const info of tabRectsRef.current.values()) {
      const oldRect = old.get(info.el);
      if (!oldRect) continue;
      const newRect = info.el.getBoundingClientRect();
      const dx = oldRect.left - newRect.left;
      if (Math.abs(dx) < 0.5) continue;
      try {
        info.el.animate(
          [
            { transform: `translateX(${dx}px)` },
            { transform: "translateX(0)" },
          ],
          {
            duration: 180,
            easing: "cubic-bezier(.2,.8,.2,1)",
            composite: "replace",
          },
        );
      } catch {
        /* Web Animations API optional */
      }
    }
  });

  // Toggle a body class while dragging — disables text selection + sets grabbing cursor globally.
  React.useEffect(() => {
    if (drag) {
      document.body.classList.add("docking-dragging");
      return () => document.body.classList.remove("docking-dragging");
    }
  }, [!!drag]);

  React.useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const x = e.clientX,
        y = e.clientY;
      // 1) Tab hit-test first (small targets, prioritize)
      let tabHit = null;
      for (const { tabsId, idx, el } of tabRectsRef.current.values()) {
        const r = el.getBoundingClientRect();
        if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
          tabHit = { tabsId, idx, rect: r };
          break;
        }
      }
      // Chrome-like reorder: if hovering over a tab in the SAME bar as source,
      // and the source tab still exists there, do an in-bar reorder live.
      if (tabHit && drag.fromTabsId === tabHit.tabsId) {
        const midX = tabHit.rect.left + tabHit.rect.width / 2;
        const insertBefore = x < midX;
        let didReorder = false;
        setRoot((prev) => {
          const srcTabs = findTabs(prev, tabHit.tabsId);
          if (!srcTabs) return prev;
          let fromIdx = -1;
          for (let i = 0; i < srcTabs.items.length; i++) {
            const it = srcTabs.items[i];
            if (drag.panelId && isItemPanel(it) && it === drag.panelId) {
              fromIdx = i;
              break;
            }
            if (
              drag.groupItem &&
              !isItemPanel(it) &&
              it.id === drag.groupItem.id
            ) {
              fromIdx = i;
              break;
            }
          }
          if (fromIdx < 0) return prev;
          const toIdx = insertBefore ? tabHit.idx : tabHit.idx + 1;
          if (toIdx === fromIdx || toIdx === fromIdx + 1) return prev;
          // Snapshot rects right before the reorder so layout effect can FLIP.
          if (!didReorder) {
            snapshotTabRects(tabHit.tabsId);
            didReorder = true;
          }
          const items = srcTabs.items.slice();
          const [moved] = items.splice(fromIdx, 1);
          const insertAt = fromIdx < toIdx ? toIdx - 1 : toIdx;
          items.splice(insertAt, 0, moved);
          let active = srcTabs.active;
          if (active === fromIdx) active = insertAt;
          else if (fromIdx < active && insertAt >= active) active = active - 1;
          else if (fromIdx > active && insertAt <= active) active = active + 1;
          return updateTabs(prev, tabHit.tabsId, (t) => ({
            ...t,
            items,
            active,
          }));
        });
        setHover(null);
        setDrag((d) => (d ? { ...d, x, y } : d));
        return;
      }
      let next = null;
      if (tabHit) {
        next = { kind: "tab", tabsId: tabHit.tabsId, itemIndex: tabHit.idx };
      } else {
        // 2) Outer panel-group hit-test (dock zones)
        let target = null;
        let bestArea = Infinity;
        for (const [tabsId, el] of groupRectsRef.current.entries()) {
          const r = el.getBoundingClientRect();
          if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
            const area = r.width * r.height;
            if (area < bestArea) {
              bestArea = area;
              target = { tabsId, rect: r };
            }
          }
        }
        if (target) {
          const { rect } = target;
          const fx = (x - rect.left) / rect.width;
          const fy = (y - rect.top) / rect.height;
          const margin = 0.25;
          let edge = "center";
          const dl = fx,
            dr = 1 - fx,
            dt = fy,
            db = 1 - fy;
          const min = Math.min(dl, dr, dt, db);
          if (min < margin) {
            if (min === dl) edge = "left";
            else if (min === dr) edge = "right";
            else if (min === dt) edge = "top";
            else edge = "bottom";
          }
          next = { kind: "group", tabsId: target.tabsId, edge };
        }
      }
      setHover(next);
      setDrag((d) => (d ? { ...d, x, y } : d));
    };
    const onUp = () => {
      setDrag((d) => {
        if (d && hoverRef.current) {
          const h = hoverRef.current;
          if (h.kind === "tab") {
            setRoot((prev) => {
              const tabsNode = findTabs(prev, h.tabsId);
              if (!tabsNode) return prev;
              const target = tabsNode.items[h.itemIndex];
              if (d.groupItem) {
                if (!isItemPanel(target) && target.id === d.groupItem.id)
                  return prev;
                const next = removeGroupItem(prev, d.groupItem.id);
                const updatedTabs = findTabs(next, h.tabsId);
                if (!updatedTabs) {
                  const first = findFirstTabs(next);
                  return first
                    ? addGroupItemToTabs(next, first.id, d.groupItem)
                    : next;
                }
                let newIdx = -1;
                for (let i = 0; i < updatedTabs.items.length; i++) {
                  const it = updatedTabs.items[i];
                  if (isItemPanel(target) && isItemPanel(it) && it === target) {
                    newIdx = i;
                    break;
                  }
                  if (
                    !isItemPanel(target) &&
                    !isItemPanel(it) &&
                    it.id === target.id
                  ) {
                    newIdx = i;
                    break;
                  }
                }
                if (newIdx < 0)
                  return addGroupItemToTabs(next, h.tabsId, d.groupItem);
                return groupWithItem(next, h.tabsId, newIdx, d.groupItem);
              }
              // Panel drag
              if (isItemPanel(target) && target === d.panelId) return prev;
              const next = removePanel(prev, d.panelId);
              const updatedTabs = findTabs(next, h.tabsId);
              if (!updatedTabs) {
                const first = findFirstTabs(next);
                return first ? addPanelToTabs(next, first.id, d.panelId) : next;
              }
              let newIdx = -1;
              for (let i = 0; i < updatedTabs.items.length; i++) {
                const it = updatedTabs.items[i];
                if (isItemPanel(target) && isItemPanel(it) && it === target) {
                  newIdx = i;
                  break;
                }
                if (
                  !isItemPanel(target) &&
                  !isItemPanel(it) &&
                  it.id === target.id
                ) {
                  newIdx = i;
                  break;
                }
              }
              if (newIdx < 0) return addPanelToTabs(next, h.tabsId, d.panelId);
              return groupWithItem(next, h.tabsId, newIdx, d.panelId);
            });
          } else if (h.kind === "group") {
            const sameSource = d.fromTabsId === h.tabsId;
            if (!(sameSource && h.edge === "center")) {
              setRoot((prev) => {
                let next = d.groupItem
                  ? removeGroupItem(prev, d.groupItem.id)
                  : removePanel(prev, d.panelId);
                const found = !!findTabs(next, h.tabsId);
                if (!found) {
                  const first = findFirstTabs(next);
                  if (!first) return next;
                  return d.groupItem
                    ? addGroupItemToTabs(next, first.id, d.groupItem)
                    : addPanelToTabs(next, first.id, d.panelId);
                }
                if (h.edge === "center") {
                  next = d.groupItem
                    ? addGroupItemToTabs(next, h.tabsId, d.groupItem)
                    : addPanelToTabs(next, h.tabsId, d.panelId);
                } else {
                  next = d.groupItem
                    ? splitAtNodeWithGroup(next, h.tabsId, h.edge, d.groupItem)
                    : splitAtNode(next, h.tabsId, h.edge, d.panelId);
                }
                return next;
              });
            }
          }
        }
        return null;
      });
      setHover(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [drag ? (drag.panelId ?? drag.groupItem?.id ?? null) : null]);

  const startDrag = React.useCallback((info: DragInfo) => setDrag(info), []);

  const value: DockingContextValue = {
    root,
    setRoot,
    drag,
    hover,
    editMode,
    setEditMode,
    registerGroup,
    registerTab,
    startDrag,
    actions: {
      removePanel: (panelId: string) => setRoot((r) => removePanel(r, panelId)),
      removeTabsGroup: (tabsId: string) =>
        setRoot((r) => removeTabsNode(r, tabsId)),
      addPanel: (panelId: string) =>
        setRoot((r) => {
          const first = findFirstTabs(r);
          return first ? addPanelToTabs(r, first.id, panelId) : r;
        }),
      setActive: (tabsId: string, idx: number) =>
        setRoot((r) => updateTabs(r, tabsId, (t) => ({ ...t, active: idx }))),
      resizeSplit: (splitId: string, sizes: number[]) =>
        setRoot((r) => updateSplit(r, splitId, (s) => ({ ...s, sizes }))),
      renameGroup: (tabsId: string, label: string) =>
        setRoot((r) => updateTabs(r, tabsId, (t) => ({ ...t, label }))),
      resetLayout: () => {
        setRoot(initialRef.current);
        if (storageKey && typeof localStorage !== "undefined") {
          try {
            localStorage.removeItem(storageKey);
          } catch {
            /* non-fatal */
          }
        }
      },
      cancelEdit: () => {
        const snap = editSnapshotRef.current;
        if (snap) setRoot(snap);
        setEditMode(false);
      },
      commitEdit: () => {
        persistRoot(rootRef.current);
        setEditMode(false);
      },
      reorderInTabs: (tabsId: string, fromIdx: number, toIdx: number) =>
        setRoot((r) =>
          updateTabs(r, tabsId, (t) => {
            if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return t;
            const items = t.items.slice();
            const [moved] = items.splice(fromIdx, 1);
            const insertAt = fromIdx < toIdx ? toIdx - 1 : toIdx;
            items.splice(insertAt, 0, moved);
            // Keep active pointing at the moved tab
            let active = t.active;
            if (active === fromIdx) active = insertAt;
            else if (fromIdx < active && insertAt >= active)
              active = active - 1;
            else if (fromIdx > active && insertAt <= active)
              active = active + 1;
            return { ...t, items, active };
          }),
        ),
    },
  };

  return <DragCtx.Provider value={value}>{children}</DragCtx.Provider>;
}

// ====== RENDERERS ======

function DockingRoot() {
  const { root } = useDragCtx();
  return (
    <div className="docking-host flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
      <Node node={root} isOuter />
    </div>
  );
}

function Node({ node, isOuter }) {
  if (node.type === "tabs") return <PanelGroup node={node} isOuter={isOuter} />;
  return <Split node={node} />;
}

function Split({ node }) {
  const { actions } = useDragCtx();
  const ref = React.useRef(null);
  const [dragging, setDragging] = React.useState(null);

  const onGutterDown = (i) => (e) => {
    e.preventDefault();
    setDragging(i);
    const rect = ref.current.getBoundingClientRect();
    const isH = node.dir === "h";
    const total = isH ? rect.width : rect.height;
    const startPos = isH ? e.clientX : e.clientY;
    const startSizes = node.sizes.slice();

    const onMove = (ev) => {
      const cur = isH ? ev.clientX : ev.clientY;
      const delta = (cur - startPos) / total;
      const next = startSizes.slice();
      next[i] = Math.max(0.1, startSizes[i] + delta);
      next[i + 1] = Math.max(0.1, startSizes[i + 1] - delta);
      const sum = next.reduce((a, b) => a + b, 0);
      actions.resizeSplit(
        node.id,
        next.map((s) => s / sum),
      );
    };
    const onUp = () => {
      setDragging(null);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const isH = node.dir === "h";

  return (
    <div
      ref={ref}
      className="docking-split grid min-h-0 min-w-0 flex-1"
      style={{
        [isH ? "gridTemplateColumns" : "gridTemplateRows"]: interleaveGutters(
          node.sizes,
        ),
      }}
    >
      {node.children.map((c, i) => (
        <React.Fragment key={c.id}>
          <div className="flex min-h-0 min-w-0">
            <Node node={c} isOuter />
          </div>
          {i < node.children.length - 1 && (
            <div
              onPointerDown={onGutterDown(i)}
              className={cn(
                "transition-colors duration-150",
                isH ? "cursor-col-resize" : "cursor-row-resize",
                dragging === i ? "bg-sf-primary/40" : "hover:bg-sf-primary/15",
              )}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function interleaveGutters(sizes) {
  const parts = [];
  sizes.forEach((s, i) => {
    parts.push(`${(s * 100).toFixed(3)}fr`);
    if (i < sizes.length - 1) parts.push("4px");
  });
  return parts.join(" ");
}

// Outer panel group — has dock zones (edges) and a header tab bar that may include nested tabs.
function PanelGroup({ node, isOuter }) {
  const ctx = useDragCtx();
  const { drag, hover, registerGroup, editMode } = ctx;
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (ref.current && isOuter) {
      registerGroup(node.id, ref.current);
      return () => registerGroup(node.id, null);
    }
  }, [node.id, registerGroup, isOuter]);

  const showDock =
    isOuter &&
    hover &&
    hover.kind === "group" &&
    hover.tabsId === node.id &&
    drag;

  const activeItem = node.items[node.active] || node.items[0];

  return (
    <div
      ref={ref}
      className="relative m-0.5 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-sf-border bg-sf-surface shadow-(--sf-shadow-sm)"
    >
      {(editMode || node.items.length > 1) && <PanelTabsBar node={node} />}
      <div
        className={cn(
          "flex min-h-0 flex-1 overflow-hidden",
          drag && "pointer-events-none",
        )}
      >
        <ItemContent item={activeItem} />
      </div>
      {showDock && <DropOverlay edge={hover.edge} />}
    </div>
  );
}

// Content of a tab item: either a panel widget or a nested tabs (rendered as sub-PanelGroup, no outer dock zone)
function ItemContent({ item }) {
  if (!item) return null;
  if (isItemPanel(item)) {
    const def = PanelDefs[item];
    if (!def) return null;
    return (
      <div
        className={cn(
          "min-h-0 flex-1",
          def.noPadding ? "overflow-hidden" : "overflow-auto p-2.5",
        )}
      >
        {def.render({ panelId: item })}
      </div>
    );
  }
  if (isItemSplit(item)) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <Split node={item} />
      </div>
    );
  }

  // Nested tabs node — render flush inside the parent (no extra chrome)
  return <NestedPanelGroup node={item} />;
}

function NestedPanelGroup({ node }) {
  const { drag, editMode } = useDragCtx();
  const activeItem = node.items[node.active] || node.items[0];
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {(editMode || node.items.length > 1) && (
        <PanelTabsBar node={node} nested />
      )}
      <div
        className={cn(
          "flex min-h-0 flex-1 overflow-hidden",
          drag && "pointer-events-none",
        )}
      >
        <ItemContent item={activeItem} />
      </div>
    </div>
  );
}

function PanelTabsBar({ node, nested }) {
  const ctx = useDragCtx();
  const { drag, startDrag, actions, hover, registerTab, editMode } = ctx;

  return (
    <div
      className={cn(
        "flex items-stretch border-b border-sf-border pr-1",
        nested ? "h-[30px] bg-sf-surface-2" : "h-9 bg-sf-surface-alt",
      )}
    >
      <div className="flex min-h-0 min-w-0 flex-1 items-stretch overflow-hidden">
        {node.items.map((it, i) => (
          <TabItem
            key={isItemPanel(it) ? `p:${it}` : `g:${it.id}`}
            tabsId={node.id}
            item={it}
            index={i}
            active={i === node.active}
            nested={nested}
            isHoverTarget={
              hover &&
              hover.kind === "tab" &&
              hover.tabsId === node.id &&
              hover.itemIndex === i &&
              drag
            }
            onSelect={() => actions.setActive(node.id, i)}
            startDrag={startDrag}
            registerTab={registerTab}
            removePanel={actions.removePanel}
            renameGroup={actions.renameGroup}
          />
        ))}
      </div>

      {editMode && (
        <div className="flex items-center gap-0.5 pl-1">
          <button
            aria-label="Supprimer le groupe"
            title="Supprimer le groupe entier"
            onClick={() => actions.removeTabsGroup(node.id)}
            className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded border-0 bg-transparent text-sf-text-muted hover:bg-red-500 hover:text-white"
            type="button"
          >
            <I.X size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function TabItem({
  tabsId,
  item,
  index,
  active,
  nested,
  isHoverTarget,
  onSelect,
  startDrag,
  registerTab,
  removePanel,
  renameGroup,
}) {
  const { drag, editMode } = useDragCtx();
  const ref = React.useRef(null);
  const [editing, setEditing] = React.useState(false);
  const [draftLabel, setDraftLabel] = React.useState("");

  React.useEffect(() => {
    if (ref.current) {
      registerTab(tabsId, index, ref.current);
      return () => registerTab(tabsId, index, null);
    }
  }, [tabsId, index, registerTab]);

  const Icn = itemIcon(item) || I.LayoutGrid;
  const title = itemTitle(item);
  const isGroup = !isItemPanel(item) && !isItemSplit(item);
  const draggablePanelId = isItemPanel(item) ? item : null;
  const draggableGroupItem = isGroup ? item : null;
  const canDrag = draggablePanelId || draggableGroupItem;

  const isDragging =
    drag &&
    drag.fromTabsId === tabsId &&
    ((draggablePanelId && drag.panelId === draggablePanelId) ||
      (draggableGroupItem &&
        drag.groupItem &&
        drag.groupItem.id === draggableGroupItem.id));

  const onPointerDown = (e) => {
    if (e.target.closest("[data-tab-close]")) return;
    if (e.target.closest("[data-tab-rename]")) return;
    if (editing) return;
    if (e.button !== 0) return;
    onSelect();
    if (!editMode) return;
    if (!canDrag) return;
    const startX = e.clientX,
      startY = e.clientY;
    let started = false;
    const tryStart = (ev) => {
      const dx = Math.abs(ev.clientX - startX);
      const dy = Math.abs(ev.clientY - startY);
      if (!started && (dx > 4 || dy > 4)) {
        started = true;
        startDrag({
          fromTabsId: tabsId,
          panelId: draggablePanelId,
          groupItem: draggableGroupItem,
          x: ev.clientX,
          y: ev.clientY,
        });
      }
    };
    const onMove = (ev) => tryStart(ev);
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      className={cn(
        "relative z-[1] inline-flex touch-none items-center gap-1.5 border-r border-sf-border py-0 select-none whitespace-nowrap",
        nested ? "pl-2.5 pr-2 text-xs" : "pl-3 pr-2.5 text-[12.5px]",
        active
          ? "font-semibold text-sf-text"
          : "font-medium text-sf-text-muted",
        isHoverTarget &&
          "bg-sf-primary/[0.18] outline outline-2 outline-offset-[-2px] outline-sf-primary",
        !isHoverTarget && "outline-none",
        !isHoverTarget && active && "bg-sf-surface",
        !isHoverTarget && !active && "bg-transparent",
        !active && !drag && !isHoverTarget && "hover:bg-black/[0.025]",
        drag
          ? "cursor-grabbing"
          : editMode && canDrag
            ? "cursor-grab"
            : "cursor-default",
        isDragging
          ? "z-[5] -translate-y-px scale-[1.015] opacity-[0.92] shadow-[0_6px_16px_-4px_rgba(17,24,39,0.18),0_2px_4px_-2px_rgba(17,24,39,0.1)] duration-150 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
          : "shadow-none duration-150 ease-out",
        "transition-[transform,box-shadow,opacity]",
      )}
    >
      {/* Group-merge hint shown when this tab is the drop target — portaled to escape tabs-bar overflow */}
      {isHoverTarget &&
        ref.current &&
        createPortal(
          (() => {
            const r = ref.current.getBoundingClientRect();
            return (
              <div
                className="animate-[dockHintIn_120ms_cubic-bezier(0.2,0.8,0.2,1)] pointer-events-none fixed z-[10000] flex translate-x-[-50%] items-center gap-1 rounded bg-sf-primary px-2 py-1 text-[11px] font-semibold whitespace-nowrap text-white shadow-[0_4px_12px_-2px_rgba(79,70,229,0.4)]"
                style={{
                  top: r.top - 30,
                  left: r.left + r.width / 2,
                }}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
                Grouper avec ce panneau
                <span className="absolute top-full left-1/2 h-0 w-0 -translate-x-1/2 border-x-[5px] border-x-transparent border-t-[5px] border-t-sf-primary" />
              </div>
            );
          })(),
          document.body,
        )}

      {active && !nested && (
        <span className="pointer-events-none absolute inset-x-0 -bottom-px h-0.5 bg-sf-primary" />
      )}
      {isGroup && (
        <span className="rounded bg-sf-primary-100 px-1 py-px text-[9px] font-bold tracking-wide text-sf-primary">
          {item.items.length}
        </span>
      )}
      <Icn size={nested ? 12 : 13} stroke={active ? 2 : 1.75} />
      {isGroup && editing ? (
        <input
          autoFocus
          value={draftLabel}
          onChange={(e) => setDraftLabel(e.target.value)}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              renameGroup(item.id, draftLabel.trim() || null);
              setEditing(false);
            } else if (e.key === "Escape") {
              setEditing(false);
            }
          }}
          onBlur={() => {
            renameGroup(item.id, draftLabel.trim() || null);
            setEditing(false);
          }}
          className={cn(
            "w-[110px] rounded border border-sf-primary bg-white px-1 py-px font-[inherit] font-semibold text-sf-text outline-none",
            nested ? "text-xs" : "text-[12.5px]",
          )}
        />
      ) : (
        <span
          onDoubleClick={(e) => {
            if (!isGroup) return;
            e.stopPropagation();
            setDraftLabel(item.label || `Groupe`);
            setEditing(true);
          }}
          title={isGroup ? "Double-cliquez pour renommer" : undefined}
        >
          {isGroup ? item.label || `Groupe ${item.items.length}` : title}
        </span>
      )}
      {isGroup && !editing && editMode && (
        <button
          data-tab-rename
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setDraftLabel(item.label || `Groupe`);
            setEditing(true);
          }}
          aria-label="Renommer le groupe"
          title="Renommer"
          type="button"
          className="ml-0.5 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded border-0 bg-sf-primary/15 text-sf-primary hover:bg-sf-primary hover:text-white"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
          </svg>
        </button>
      )}
      {isItemPanel(item) && editMode && (
        <button
          data-tab-close
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            removePanel(item);
          }}
          aria-label="Fermer"
          type="button"
          className="ml-1 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded border-0 bg-transparent text-sf-text-muted hover:bg-sf-border hover:text-sf-text"
        >
          <I.X size={13} />
        </button>
      )}
    </div>
  );
}

function DropOverlay({ edge }) {
  const positions = {
    center: { inset: 6 },
    left: { left: 6, top: 6, bottom: 6, width: "40%" },
    right: { right: 6, top: 6, bottom: 6, width: "40%" },
    top: { top: 6, left: 6, right: 6, height: "40%" },
    bottom: { bottom: 6, left: 6, right: 6, height: "40%" },
  };
  const labels = {
    center: "Nouvel onglet",
    left: "Scinder à gauche",
    right: "Scinder à droite",
    top: "Scinder en haut",
    bottom: "Scinder en bas",
  };
  return (
    <>
      <div
        className="pointer-events-none absolute z-[5] rounded-lg border-2 border-sf-primary bg-sf-primary/15 transition-all duration-150"
        style={positions[edge]}
      />
      <div className="pointer-events-none absolute top-1/2 left-1/2 z-[6] -translate-x-1/2 -translate-y-1/2 rounded bg-sf-primary px-2.5 py-1 text-[11px] font-semibold tracking-wide text-white uppercase whitespace-nowrap">
        {labels[edge]}
      </div>
    </>
  );
}

function DragGhost() {
  const { drag } = useDragCtx();
  if (!drag) return null;
  if (drag.groupItem) {
    const count = drag.groupItem.items?.length ?? 0;
    const label = drag.groupItem.label || `Groupe (${count})`;
    return (
      <div
        className="pointer-events-none fixed z-[10000] inline-flex items-center gap-1.5 rounded-md bg-sf-primary px-3 py-1.5 text-[12.5px] font-semibold text-white shadow-[0_8px_24px_rgba(79,70,229,0.4)]"
        style={{ left: drag.x + 12, top: drag.y + 8 }}
      >
        <I.LayoutGrid size={14} stroke={2} />
        <span>{label}</span>
      </div>
    );
  }
  const def = PanelDefs[drag.panelId];
  if (!def) return null;
  const Icn = def.icon;
  return (
    <div
      className="pointer-events-none fixed z-[10000] inline-flex items-center gap-1.5 rounded-md bg-sf-primary px-3 py-1.5 text-[12.5px] font-semibold text-white shadow-[0_8px_24px_rgba(79,70,229,0.4)]"
      style={{ left: drag.x + 12, top: drag.y + 8 }}
    >
      <Icn size={14} stroke={2} />
      <span>{def.title}</span>
    </div>
  );
}

export {
  DockingProvider,
  DockingRoot,
  DragGhost,
  makeTabs,
  makeSplit,
  collectPanelIds,
  DragCtx,
};
